import { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { Parent } from '@/types/models';

interface EmergencyContact {
  id?: string;
  name: string;
  relationship: string;
  phone: string;
  is_primary: boolean;
}

interface CommunicationPreferences {
  email_notifications: boolean;
  sms_notifications: boolean;
  class_reminders: boolean;
  billing_alerts: boolean;
  event_announcements: boolean;
  preferred_contact_method: 'email' | 'sms' | 'both';
}

interface ParentFormData {
  first_name: string | undefined;
  last_name: string | undefined;
  email: string | undefined;
  phone: string | undefined;
  is_primary: boolean | undefined;
  is_active: boolean | undefined;
  address_street: string | undefined;
  address_city: string | undefined;
  address_state: string | undefined;
  address_zip: string | undefined;
  address_country: string | undefined;
  profile_photo_url: string | undefined;
  emergency_contacts: EmergencyContact[] | undefined;
  communication_preferences: CommunicationPreferences | undefined;
  notes: string | undefined;
  portal_access_enabled: boolean | undefined;
  can_view_billing: boolean | undefined;
  can_manage_children: boolean | undefined;
}

interface UseParentFormProps {
  parent: Parent | null;
  onUpdated: (parentId: string, updatedData: Partial<Parent>) => void;
}

export const useParentForm = ({ parent, onUpdated }: UseParentFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<ParentFormData>({
    first_name: parent?.first_name || '',
    last_name: parent?.last_name || '',
    email: parent?.email || '',
    phone: parent?.phone || '',
    is_primary: parent?.is_primary || false,
    is_active: parent?.is_active !== false,
    address_street: parent?.address_street || '',
    address_city: parent?.address_city || '',
    address_state: parent?.address_state || '',
    address_zip: parent?.address_zip || '',
    address_country: parent?.address_country || 'United States',
    profile_photo_url: parent?.profile_photo_url || '',
    emergency_contacts: [
      {
        name: '',
        relationship: '',
        phone: '',
        is_primary: true
      }
    ],
    communication_preferences: {
      email_notifications: true,
      sms_notifications: false,
      class_reminders: true,
      billing_alerts: true,
      event_announcements: true,
      preferred_contact_method: 'email'
    },
    notes: '',
    portal_access_enabled: parent?.is_active !== false,
    can_view_billing: true,
    can_manage_children: true
  });

  // Update form data when parent prop changes
  useEffect(() => {
    if (parent) {
      setFormData({
        first_name: parent.first_name || '',
        last_name: parent.last_name || '',
        email: parent.email || '',
        phone: parent.phone || '',
        is_primary: parent.is_primary || false,
        is_active: parent.is_active !== false,
        address_street: parent.address_street || '',
        address_city: parent.address_city || '',
        address_state: parent.address_state || '',
        address_zip: parent.address_zip || '',
        address_country: parent.address_country || 'United States',
        profile_photo_url: parent.profile_photo_url || '',
        emergency_contacts: [
          {
            name: '',
            relationship: '',
            phone: '',
            is_primary: true
          }
        ],
        communication_preferences: {
          email_notifications: true,
          sms_notifications: false,
          class_reminders: true,
          billing_alerts: true,
          event_announcements: true,
          preferred_contact_method: 'email'
        },
        notes: '',
        portal_access_enabled: parent.is_active !== false,
        can_view_billing: true,
        can_manage_children: true
      });
    }
  }, [parent]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleSwitchChange = useCallback((name: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  }, []);

  const handleImageUpload = useCallback(async (file: File) => {
    // TODO: Implement image upload logic
    console.log('Image upload:', file);
  }, []);

  const saveParent = useCallback(async () => {
    if (!parent) return;
    
    try {
      setIsSubmitting(true);

      // Create update payload with only changed fields
      const updateData: Partial<Parent> = {};
      
      // Check for changes and add to update payload
      if (formData.first_name !== parent.first_name) {
        updateData.first_name = formData.first_name || '';
      }
      if (formData.last_name !== parent.last_name) {
        updateData.last_name = formData.last_name || '';
      }
      if (formData.email !== parent.email) {
        updateData.email = formData.email || undefined;
      }
      if (formData.phone !== parent.phone) {
        updateData.phone = formData.phone || undefined;
      }
      if (formData.is_primary !== parent.is_primary) {
        updateData.is_primary = formData.is_primary;
      }
      if (formData.is_active !== parent.is_active) {
        updateData.is_active = formData.is_active;
      }
      if (formData.address_street !== parent.address_street) {
        updateData.address_street = formData.address_street || undefined;
      }
      if (formData.address_city !== parent.address_city) {
        updateData.address_city = formData.address_city || undefined;
      }
      if (formData.address_state !== parent.address_state) {
        updateData.address_state = formData.address_state || undefined;
      }
      if (formData.address_zip !== parent.address_zip) {
        updateData.address_zip = formData.address_zip || undefined;
      }
      if (formData.address_country !== parent.address_country) {
        updateData.address_country = formData.address_country || undefined;
      }

      // Only update if there are changes
      if (Object.keys(updateData).length === 0) {
        toast.info('No changes to save');
        setIsSubmitting(false);
        return;
      }

      console.log('Updating parent with data:', updateData);
      
      // Call the provided onUpdated function
      onUpdated(parent.id, updateData);
      toast.success('Parent updated successfully');
      
    } catch (error) {
      console.error('Error updating parent:', error);
      toast.error('Failed to update parent', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, parent, onUpdated]);

  return {
    formData,
    isSubmitting,
    handleChange,
    handleSwitchChange,
    handleImageUpload,
    saveParent
  };
};